import React, { useEffect, useState, useRef } from "react";
import { getDatabase, ref, onValue, update, get, remove } from "firebase/database";
import { db } from "./firebase/config";
import { ClipLoader } from "react-spinners";
import { FiXCircle, FiFacebook, FiUsers, FiEdit3, FiTrash2, FiMessageCircle } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  getSocialAccountKey,
  getProductAccounts,
  getAccountPosition,
  loadConnectedSocialAccounts,
  toSelectedAccountPayload,
} from "./utils/socialChannels";
import {
  PRODUCT_AUTO_POST_CONFIG_SUMMARY_ROOT,
  PRODUCT_AUTO_POST_SOURCE_LOCAL,
  isWooProductAutoPostConfig,
} from "./utils/rtdbCronIndex";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Component sản phẩm trong nhóm fanpage với khả năng sắp xếp
const SortableProductItem = ({
  product,
  index,
  isSortMode,
  onProductClick,
  isEditMode = false,
  isSelected = false,
  onSelectProduct,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.8 : 1,
    boxShadow: isDragging
      ? "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)"
      : "",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isSortMode ? { ...attributes, ...listeners } : {})}
      className={`bg-gray-50 rounded-lg shadow-sm p-4 relative transform transition duration-200 ease-in-out 
        ${
          isSortMode
            ? isDragging
              ? "border-2 border-blue-500 bg-white"
              : "hover:border-2 hover:border-blue-300 cursor-grab bg-white"
            : isEditMode
            ? isSelected
              ? "border-2 border-green-500 bg-green-50"
              : "hover:border-2 hover:border-gray-300 cursor-pointer bg-white"
            : "hover:scale-102 hover:shadow-md cursor-pointer bg-white"
        }`}
      onClick={() => {
        if (isSortMode) return;
        if (isEditMode) {
          onSelectProduct && onSelectProduct(product.id);
        } else {
          onProductClick(product);
        }
      }}
    >
      {/* Checkbox cho chế độ chỉnh sửa */}
      {isEditMode && (
        <div className="absolute top-2 right-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectProduct && onSelectProduct(product.id);
            }}
            className="w-5 h-5 text-green-600 bg-white border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
          />
        </div>
      )}

      {/* Hiển thị vị trí trong fanpage */}
      <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs flex items-center">
        <span className="mr-1">#{product.fanpagePosition || index + 1}</span>
        {isSortMode && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </div>

      <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6 truncate">
        {product.name}
      </h3>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {Array.isArray(product.images) &&
          product.images
            .slice(0, 2)
            .map((image, imgIndex) => (
              <img
                key={imgIndex}
                src={image}
                alt={`Product ${product.id} Image ${imgIndex}`}
                className="w-full h-24 object-contain bg-gray-100 rounded"
              />
            ))}
      </div>

      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600">{product.images?.length || 0} ảnh</span>
        <div
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            product.isActive
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {product.isActive ? "Đang chạy" : "Đã tắt"}
        </div>
      </div>

      {product.isPosted && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
          Đã đăng
        </div>
      )}
    </div>
  );
};

// Component hiển thị sản phẩm dạng danh sách cho chế độ sắp xếp
const SortableProductListItem = ({ product, index, onProductClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: product.id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? "#f3f4f6" : "white",
    touchAction: "none",
    maxWidth: "100%",
  };

  // Tăng độ dài hiển thị cho tên sản phẩm
  const truncateName = (name) => {
    if (name.length > 60) {
      return name.substring(0, 58) + "...";
    }
    return name;
  };

  // Tăng độ dài hiển thị cho tên fanpage
  const truncateFanpageName = (name) => {
    if (name.length > 35) {
      return name.substring(0, 33) + "...";
    }
    return name;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-start p-2 border rounded-lg ${
        isDragging
          ? "shadow-lg border-blue-400"
          : "border-gray-200 hover:border-blue-300"
      } transition-all duration-200 touch-manipulation w-full overflow-hidden`}
    >
      {/* Số thứ tự */}
      <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 text-blue-800 rounded-full mr-2 flex-shrink-0 touch-manipulation mt-0.5">
        <span className="text-xs font-medium">{index + 1}</span>
      </div>

      {/* Thông tin sản phẩm */}
      <div className="flex-grow min-w-0 mr-2 overflow-hidden">
        <div className="font-medium text-gray-800 text-xs sm:text-sm leading-tight mb-0.5">
          {truncateName(product.name)}
        </div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {product.fanpages &&
            product.fanpages.length > 0 &&
            product.fanpages.slice(0, 1).map((page, pageIndex) => (
              <span
                key={pageIndex}
                className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded-sm truncate max-w-[200px] sm:max-w-[250px] leading-tight"
              >
                {truncateFanpageName(page.name)}
              </span>
            ))}
          {product.fanpages && product.fanpages.length > 1 && (
            <span className="text-xs text-gray-500 leading-tight">
              +{product.fanpages.length - 1}
            </span>
          )}
        </div>
      </div>

      {/* Trạng thái và nút kéo */}
      <div className="flex items-center flex-shrink-0 ml-auto">
        <div
          className={`px-1.5 py-0.5 rounded text-xs ${
            product.isActive
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {product.isActive ? "On" : "Off"}
        </div>

        <div className="ml-1.5 text-gray-400 cursor-grab bg-gray-100 p-1 rounded">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Thêm component SortableGridItem cho chế độ grid
const SortableGridItem = ({ product, index, onProductClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: product.id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
    opacity: isDragging ? 0.8 : 1,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-white rounded-lg shadow-md p-4 ${
        isDragging ? "shadow-xl border-2 border-blue-500" : ""
      }`}
    >
      {/* Thêm div bao bọc để xử lý sự kiện kéo thả */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing touch-manipulation rounded-lg"
        style={{ touchAction: "none" }}
      >
        {/* Vùng trong suốt để kéo thả */}
        <div
          className="absolute inset-0"
          style={{ pointerEvents: "none" }}
        ></div>
      </div>

      {/* Hiển thị chỉ báo kéo thả */}
      <div className="absolute top-0 left-0 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-br-md z-20 flex items-center">
        <span>{index + 1}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3 ml-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16m-7 6h7"
          />
        </svg>
      </div>

      <h2 className="text-xl font-bold text-gray-800 mb-2 truncate whitespace-nowrap overflow-hidden">
        {product.name}
      </h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {Array.isArray(product.images) &&
          product.images
            .slice(0, 2)
            .map((image, imgIndex) => (
              <img
                key={imgIndex}
                src={image}
                alt={`Product ${product.id} Image ${imgIndex}`}
                className="w-full h-32 object-contain bg-gray-100"
              />
            ))}
      </div>
      {product.fanpages && product.fanpages.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {product.fanpages.map((page, pageIndex) => (
              <span
                key={pageIndex}
                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
              >
                {page.name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {product.images?.length || 0} ảnh
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              product.isActive
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {product.isActive ? "🔵 Đang chạy" : "⚪ Đã tắt"}
          </div>
        </div>
      </div>
      {product.isPosted && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
          Đã đăng
        </div>
      )}
    </div>
  );
};

const ProductsScreen = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fanpages, setFanpages] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState({});
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFanpage, setFilterFanpage] = useState("all");
  const [isFanpageDropdownOpen, setIsFanpageDropdownOpen] = useState(false);
  const fanpageDropdownRef = useRef(null);
  const [isSortMode, setIsSortMode] = useState({});
  const [autoPostConfigs, setAutoPostConfigs] = useState({}); // Lưu trữ thông tin thiết lập tự động cho từng fanpage
  const [configSelectionModal, setConfigSelectionModal] = useState({
    isOpen: false,
    fanpageId: null,
    fanpageName: "",
    configs: [],
    fanpageProducts: [],
  }); // State quản lý modal chọn thiết lập
  
  // State cho chức năng chỉnh sửa và xóa hàng loạt
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectedFanpages, setSelectedFanpages] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Hàm tải thông tin thiết lập tự động đăng bài từ summary nhẹ (không scan full posts)
  const loadAutoPostConfigs = async () => {
    try {
      const database = getDatabase();
      const userId = localStorage.getItem("userId");
      const summaryRef = ref(database, `${PRODUCT_AUTO_POST_CONFIG_SUMMARY_ROOT}/${userId}`);
      const snapshot = await get(summaryRef);

      if (snapshot.exists()) {
        const allSummaries = snapshot.val();
        const configsByAccountKey = {};
        const summaryEntries = [];

        // allSummaries structure: { accountKey: { postId: summary } }
        Object.entries(allSummaries).forEach(([accountKey, configs]) => {
          if (!configs || typeof configs !== "object") return;
          Object.entries(configs).forEach(([postId, summary]) => {
            summaryEntries.push({ accountKey, postId, summary });
          });
        });

        const resolvedEntries = await Promise.all(
          summaryEntries.map(async (entry) => {
            if (
              entry.summary?.productSource === PRODUCT_AUTO_POST_SOURCE_LOCAL
            ) {
              return entry;
            }

            const postSnapshot = await get(
              ref(database, `posts/${userId}/${entry.postId}`)
            ).catch(() => null);

            return {
              ...entry,
              postConfig: postSnapshot?.exists() ? postSnapshot.val() : null,
            };
          })
        );

        resolvedEntries.forEach(
          ({ accountKey, postId, summary, postConfig }) => {
            if (
              isWooProductAutoPostConfig(summary) ||
              (postConfig && isWooProductAutoPostConfig(postConfig)) ||
              postConfig === null
            ) {
              return;
            }

            if (!configsByAccountKey[accountKey]) {
              configsByAccountKey[accountKey] = [];
            }

            configsByAccountKey[accountKey].push({
              id: postId,
              ...summary,
            });
          }
        );

        setAutoPostConfigs(configsByAccountKey);
      }
    } catch (error) {
      console.error("Lỗi khi tải thiết lập tự động:", error);
    }
  };

  // Cấu hình sensors cho DndContext
  const sensors = useSensors(
    useSensor(TouchSensor, {
      // Tăng ngưỡng kích hoạt để tránh kích hoạt vô tình
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor, {
      // Cấu hình cho chuột và bút stylus
      activationConstraint: {
        distance: 8, // Khoảng cách tối thiểu để kích hoạt kéo
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Hàm nhóm sản phẩm theo account (hỗ trợ cả selectedAccounts và fanpages legacy)
  const groupProductsByFanpage = (productList) => {
    const grouped = {};
    const uniqueFanpagesMap = new Map();

    productList.forEach((product) => {
      const accounts = getProductAccounts(product);

      if (accounts.length > 0) {
        accounts.forEach((account) => {
          const accountKey = getSocialAccountKey(account);
          if (!accountKey) return;

          if (!uniqueFanpagesMap.has(accountKey)) {
            uniqueFanpagesMap.set(accountKey, {
              id: account.accountId,
              name: account.name,
              platform: account.platform,
              access_token: account.access_token,
            });
          }

          if (!grouped[accountKey]) {
            grouped[accountKey] = {
              fanpage: {
                id: account.accountId,
                name: account.name,
                platform: account.platform,
                access_token: account.access_token,
              },
              products: [],
            };
          }

          const fanpagePosition = getAccountPosition(product, account);

          grouped[accountKey].products.push({
            ...product,
            fanpagePosition,
          });
        });
      }
    });

    // Sắp xếp sản phẩm trong mỗi group theo fanpagePosition
    Object.keys(grouped).forEach((accountKey) => {
      grouped[accountKey].products.sort(
        (a, b) => a.fanpagePosition - b.fanpagePosition
      );

      grouped[accountKey].products = grouped[accountKey].products.map(
        (product, index) => ({
          ...product,
          fanpagePosition: index + 1,
        })
      );
    });

    return { grouped, uniqueFanpagesMap };
  };

  // Tải danh sách accounts tách khỏi products listener
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const database = getDatabase();
    loadConnectedSocialAccounts(database, userId)
      .then((allAccounts) => {
        setFanpages(allAccounts);
      })
      .catch((error) => {
        console.error("Lỗi khi lấy danh sách accounts:", error);
      });

    loadAutoPostConfigs();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const productsRef = ref(db, `products/${userId}`);

    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      let productList = [];

      if (data) {
        productList = Object.entries(data).map(([id, product]) => ({
          ...product,
          id,
          position: product.position || 9999,
          fanpagePositions: product.fanpagePositions || {},
        }));

        productList.sort((a, b) => a.position - b.position);
      }

      const { grouped, uniqueFanpagesMap } =
        groupProductsByFanpage(productList);

      // Thêm accounts không có sản phẩm
      fanpages.forEach((account) => {
        const accountKey = getSocialAccountKey(account);
        if (accountKey && !grouped[accountKey]) {
          grouped[accountKey] = {
            fanpage: {
              id: account.accountId || account.id,
              name: account.name,
              platform: account.platform || "facebook",
              access_token: account.access_token,
            },
            products: [],
          };
        }
      });

      setProducts(productList);
      setGroupedProducts(grouped);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [fanpages]);

  // Xử lý drag & drop cho từng nhóm fanpage
  const handleGroupedDragEnd = (event, fanpageId) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const fanpageGroup = groupedProducts[fanpageId];
    if (!fanpageGroup) return;

    const oldIndex = fanpageGroup.products.findIndex(
      (product) => product.id === active.id
    );
    const newIndex = fanpageGroup.products.findIndex(
      (product) => product.id === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      // Tạo bản sao của danh sách sản phẩm trong fanpage
      const newProducts = [...fanpageGroup.products];
      const [movedItem] = newProducts.splice(oldIndex, 1);
      newProducts.splice(newIndex, 0, movedItem);

      // Cập nhật fanpagePosition cho tất cả sản phẩm trong fanpage
      const updatedProducts = newProducts.map((product, index) => ({
        ...product,
        fanpagePosition: index + 1,
      }));

      // Cập nhật state groupedProducts
      const newGroupedProducts = {
        ...groupedProducts,
        [fanpageId]: {
          ...fanpageGroup,
          products: updatedProducts,
        },
      };
      setGroupedProducts(newGroupedProducts);

      // Cập nhật vào Firebase
      const userId = localStorage.getItem("userId");
      const updates = {};

      updatedProducts.forEach((product) => {
        // Cập nhật fanpagePositions cho sản phẩm
        const currentFanpagePositions = product.fanpagePositions || {};
        currentFanpagePositions[fanpageId] = product.fanpagePosition;

        updates[`products/${userId}/${product.id}/fanpagePositions`] =
          currentFanpagePositions;
      });

      update(ref(db), updates)
        .then(() => {})
        .catch((error) => {
          toast.error("Lỗi khi lưu thứ tự: " + error.message);
        });
    }
  };

  // Hàm lọc sản phẩm theo tìm kiếm và trạng thái cho từng fanpage
  const getFilteredProductsForFanpage = (fanpageProducts) => {
    return fanpageProducts.filter((product) => {
      const matchesSearch =
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.fanpages?.some((page) =>
          page.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesStatus =
        filterStatus === "all"
          ? true
          : filterStatus === "active"
          ? product.isActive
          : !product.isActive;

      return matchesSearch && matchesStatus;
    });
  };

  // Hàm lọc fanpage theo filter fanpage
  const getFilteredFanpages = () => {
    if (filterFanpage === "all") {
      return Object.entries(groupedProducts);
    }
    return Object.entries(groupedProducts).filter(([fanpageId]) => fanpageId === filterFanpage);
  };

  // useEffect để xử lý click outside cho dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
      if (fanpageDropdownRef.current && !fanpageDropdownRef.current.contains(event.target)) {
        setIsFanpageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProductClick = (product) => {
    // Kiểm tra xem có chế độ sắp xếp nào đang hoạt động không
    const anySortModeActive = Object.values(isSortMode).some((mode) => mode);
    if (anySortModeActive) return;

    navigate("/product-create", {
      state: { product },
    });
  };

  // Toggle chế độ sắp xếp cho từng fanpage
  const toggleSortModeForFanpage = (fanpageId) => {
    setIsSortMode((prev) => ({
      ...prev,
      [fanpageId]: !prev[fanpageId],
    }));
  };

  // Hàm xử lý chọn/bỏ chọn sản phẩm
  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Hàm xử lý chọn/bỏ chọn tất cả sản phẩm của fanpage
  const handleSelectFanpage = (fanpageId, fanpageProducts) => {
    const fanpageProductIds = fanpageProducts.map(p => p.id);
    const isAllSelected = fanpageProductIds.every(id => selectedProducts.has(id));
    
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (isAllSelected) {
        // Bỏ chọn tất cả sản phẩm của fanpage
        fanpageProductIds.forEach(id => newSet.delete(id));
        setSelectedFanpages(prevFanpages => {
          const newFanpageSet = new Set(prevFanpages);
          newFanpageSet.delete(fanpageId);
          return newFanpageSet;
        });
      } else {
        // Chọn tất cả sản phẩm của fanpage
        fanpageProductIds.forEach(id => newSet.add(id));
        setSelectedFanpages(prevFanpages => new Set(prevFanpages).add(fanpageId));
      }
      return newSet;
    });
  };

  // Hàm toggle chế độ chỉnh sửa
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) {
      // Thoát chế độ chỉnh sửa - reset selections
      setSelectedProducts(new Set());
      setSelectedFanpages(new Set());
    }
  };

  // Hàm xóa sản phẩm được chọn
  const handleDeleteSelectedProducts = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();
      
      // Xóa từng sản phẩm
      const deletePromises = Array.from(selectedProducts).map(productId => {
        return remove(ref(db, `products/${userId}/${productId}`));
      });
      
      await Promise.all(deletePromises);
      
      toast.success(`Đã xóa ${selectedProducts.size} sản phẩm thành công!`);
      
      // Reset selections và thoát chế độ chỉnh sửa
      setSelectedProducts(new Set());
      setSelectedFanpages(new Set());
      setIsEditMode(false);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Lỗi khi xóa sản phẩm:", error);
      toast.error("Có lỗi xảy ra khi xóa sản phẩm: " + error.message);
    }
  };

  // CSS cho chế độ sắp xếp
  useEffect(() => {
    const anySortModeActive = Object.values(isSortMode).some((mode) => mode);

    if (anySortModeActive) {
      document.body.classList.add("sorting-mode");

      const style = document.createElement("style");
      style.id = "grouped-sorting-style";
      style.innerHTML = `
        .sorting-mode {
          user-select: none;
        }
        .sorting-mode [data-sortable="true"] {
          cursor: grab !important;
        }
        .sorting-mode [data-sortable="true"]:active {
          cursor: grabbing !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.body.classList.remove("sorting-mode");
        const styleElement = document.getElementById("grouped-sorting-style");
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [isSortMode]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <div className="flex justify-between items-center mb-2 sm:mb-3 md:mb-6">
          <h1 className="text-lg sm:text-xl md:text-3xl font-bold text-gray-800">
            Danh Sách Sản Phẩm
          </h1>
          
          <div className="flex items-center gap-3">
            {isEditMode && selectedProducts.size > 0 && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <FiTrash2 className="h-4 w-4" />
                <span className="hidden sm:inline">Xóa ({selectedProducts.size})</span>
                <span className="sm:hidden">{selectedProducts.size}</span>
              </button>
            )}
            
            <button
              onClick={toggleEditMode}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                isEditMode
                  ? "bg-gray-500 text-white hover:bg-gray-600"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              <FiEdit3 className="h-4 w-4" />
              <span className="hidden sm:inline">
                {isEditMode ? "Hủy chỉnh sửa" : "Chỉnh sửa"}
              </span>
              <span className="sm:hidden">
                {isEditMode ? "Hủy" : "Sửa"}
              </span>
            </button>
          </div>
        </div>

        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-3 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-3 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm hoặc tên tài khoản..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="md:w-60 lg:w-72 relative" ref={fanpageDropdownRef}>
            <button
              onClick={() => setIsFanpageDropdownOpen(!isFanpageDropdownOpen)}
              className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center text-sm ${
                filterFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="truncate flex items-center gap-2 text-sm">
                {filterFanpage === "all" ? (
                  <span>Tất cả tài khoản</span>
                ) : (() => {
                  const match = fanpages.find(f => getSocialAccountKey(f) === filterFanpage);
                  if (match) {
                    const Icon = match.platform === "zalo" ? FiMessageCircle : FiFacebook;
                    const iconColor = match.platform === "zalo" ? "text-blue-500" : "text-blue-600";
                    return (
                      <>
                        <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
                        <span className="truncate">{match.name}</span>
                      </>
                    );
                  }
                  return <span>Tài khoản</span>;
                })()}
              </div>
              <svg
                className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${
                  isFanpageDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isFanpageDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setFilterFanpage("all");
                    setIsFanpageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 text-sm ${filterFanpage === "all" ? "bg-blue-50 text-blue-700 font-medium" : ""}`}
                >
                  Tất cả tài khoản
                </button>
                {fanpages.map((fanpage) => {
                  const accountKey = getSocialAccountKey(fanpage);
                  const Icon = fanpage.platform === "zalo" ? FiMessageCircle : FiFacebook;
                  const iconColor = fanpage.platform === "zalo" ? "text-blue-500" : "text-blue-600";
                  return (
                    <button
                      key={accountKey}
                      onClick={() => {
                        setFilterFanpage(accountKey);
                        setIsFanpageDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2 ${filterFanpage === accountKey ? "bg-blue-50 text-blue-700 font-medium" : ""}`}
                      title={fanpage.name}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
                      <span className="truncate">{fanpage.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="md:w-48 lg:w-56 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="truncate flex items-center gap-2 text-sm">
                {filterStatus === "all" ? (
                  <span>Tất cả trạng thái</span>
                ) : filterStatus === "active" ? (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                    <span className="truncate">Đang chạy</span>
                  </>
                ) : (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-400 flex-shrink-0"></span>
                    <span className="truncate">Đã tắt</span>
                  </>
                )}
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${filterStatus === "all" ? "bg-blue-50 text-blue-700 font-medium" : ""}`}
                >
                  Tất cả trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm ${filterStatus === "active" ? "bg-blue-50 text-blue-700 font-medium" : ""}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                  <span className="truncate">Đang chạy</span>
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm ${filterStatus === "inactive" ? "bg-blue-50 text-blue-700 font-medium" : ""}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-400 flex-shrink-0"></span>
                  <span className="truncate">Đã tắt</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center">
            <ClipLoader size={50} color={"#123abc"} />
          </div>
        ) : Object.keys(groupedProducts).length === 0 ||
          getFilteredFanpages().every(([fanpageId, group]) => {
            const filteredProducts = getFilteredProductsForFanpage(
              group.products
            );
            return filteredProducts.length === 0;
          }) ? (
          <div className="text-center text-gray-500 py-8">
            {products.length === 0
              ? "Không có sản phẩm nào được tạo."
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div className="space-y-6">
            {getFilteredFanpages()
              .filter(([fanpageId, group]) => {
                // Hiển thị tất cả fanpage, kể cả không có sản phẩm
                if (group.products.length === 0) {
                  return true; // Luôn hiển thị fanpage không có sản phẩm
                }

                // Lọc theo tìm kiếm và trạng thái cho fanpage có sản phẩm
                const filteredProducts = getFilteredProductsForFanpage(
                  group.products
                );
                return filteredProducts.length > 0;
              })
              .map(([fanpageId, group]) => {
                const filteredProducts = getFilteredProductsForFanpage(
                  group.products
                );
                const hasProducts = group.products.length > 0;

                return (
                  <div
                    key={fanpageId}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    {/* Header của nhóm fanpage */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center text-white">
                          {isEditMode && hasProducts && (
                            <div className="mr-3">
                              <input
                                type="checkbox"
                                checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.has(p.id))}
                                onChange={() => handleSelectFanpage(fanpageId, filteredProducts)}
                                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              />
                            </div>
                          )}
                          {group.fanpage.platform === "zalo" ? (
                            <FiMessageCircle className="mr-3 text-xl" />
                          ) : (
                            <FiFacebook className="mr-3 text-xl" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold">
                                {group.fanpage.name}
                              </h3>
                              {/* Badge hiển thị trạng thái thiết lập tự động */}
                              {(() => {
                                const accountKey = `${group.fanpage.platform || "facebook"}:${group.fanpage.id}`;
                                const configs =
                                  autoPostConfigs[accountKey] || autoPostConfigs[group.fanpage.id] || [];
                                const activeConfigs = configs.filter(
                                  (config) => config.autoPostEnabled
                                );
                                const totalConfigs = configs.length;

                                let tooltipText = "";

                                if (totalConfigs === 0) {
                                  tooltipText =
                                    "Fanpage này chưa có thiết lập tự động đăng bài nào. Nhấn 'Tạo thiết lập' để bắt đầu.";
                                  return (
                                    <span
                                      className="px-2 py-1 bg-gray-500 bg-opacity-50 text-xs rounded-full cursor-help"
                                      title={tooltipText}
                                    >
                                      Chưa có thiết lập
                                    </span>
                                  );
                                } else if (activeConfigs.length === 0) {
                                  const configDetails = configs
                                    .map(
                                      (config) =>
                                        `• ${
                                          config.fanpageName || "Thiết lập"
                                        }: Đã tắt`
                                    )
                                    .join("\n");
                                  tooltipText = `Có ${totalConfigs} thiết lập nhưng tất cả đều đã tắt:\n${configDetails}\n\nNhấn 'Quản lý thiết lập' để bật lại.`;
                                  return (
                                    <span
                                      className="px-2 py-1 bg-red-500 bg-opacity-50 text-xs rounded-full cursor-help"
                                      title={tooltipText}
                                    >
                                      {totalConfigs} thiết lập đã tắt
                                    </span>
                                  );
                                } else {
                                  const activeDetails = activeConfigs
                                    .map(
                                      (config) =>
                                        `• ${
                                          config.fanpageName || "Thiết lập"
                                        }: Đang chạy (${
                                          config.selectedTimes?.length || 0
                                        } lần/ngày)`
                                    )
                                    .join("\n");
                                  const inactiveConfigs = configs.filter(
                                    (config) => !config.autoPostEnabled
                                  );
                                  const inactiveDetails =
                                    inactiveConfigs.length > 0
                                      ? "\n\nĐã tắt:\n" +
                                        inactiveConfigs
                                          .map(
                                            (config) =>
                                              `• ${
                                                config.fanpageName ||
                                                "Thiết lập"
                                              }: Đã tắt`
                                          )
                                          .join("\n")
                                      : "";
                                  tooltipText = `Thiết lập đang hoạt động:\n${activeDetails}${inactiveDetails}\n\nNhấn 'Quản lý thiết lập' để chỉnh sửa.`;
                                  return (
                                    <span
                                      className="px-2 py-1 bg-green-500 bg-opacity-50 text-xs rounded-full cursor-help"
                                      title={tooltipText}
                                    >
                                      {activeConfigs.length}/{totalConfigs} đang
                                      chạy
                                    </span>
                                  );
                                }
                              })()}
                            </div>
                            <p className="text-blue-100 text-sm">
                              {hasProducts ? (
                                <>
                                  {filteredProducts.length} sản phẩm
                                  {filteredProducts.length !==
                                    group.products.length &&
                                    ` (từ ${group.products.length} sản phẩm)`}
                                </>
                              ) : (
                                "Chưa có sản phẩm"
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Nút thiết lập đăng bài tự động */}
                          {(() => {
                            const accountKey = `${group.fanpage.platform || "facebook"}:${group.fanpage.id}`;
                            const configs =
                              autoPostConfigs[accountKey] || autoPostConfigs[group.fanpage.id] || [];
                            const activeConfigs = configs.filter(
                              (config) => config.autoPostEnabled
                            );
                            const totalConfigs = configs.length;

                            let buttonText, buttonIcon, buttonClass;

                            if (totalConfigs === 0) {
                              buttonText = "Tạo thiết lập";
                              buttonIcon = (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                  />
                                </svg>
                              );
                              buttonClass =
                                "px-4 py-2 rounded-lg border-2 border-white border-opacity-50 hover:border-opacity-100 bg-transparent text-white transition-colors flex items-center gap-2";
                            } else if (activeConfigs.length === 0) {
                              buttonText = "Quản lý thiết lập";
                              buttonIcon = (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              );
                              buttonClass =
                                "px-4 py-2 rounded-lg border-2 border-orange-300 hover:border-orange-200 bg-orange-500 bg-opacity-20 text-white transition-colors flex items-center gap-2";
                            } else {
                              buttonText = "Quản lý thiết lập";
                              buttonIcon = (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              );
                              buttonClass =
                                "px-4 py-2 rounded-lg border-2 border-green-300 hover:border-green-200 bg-green-500 bg-opacity-20 text-white transition-colors flex items-center gap-2";
                            }

                            return (
                              <button
                                onClick={async () => {
                                  // Lấy danh sách sản phẩm của fanpage này
                                  const fanpageProducts = filteredProducts.map(
                                    (product) => ({
                                      id: product.id,
                                      name: product.name,
                                      description: product.description,
                                      images: product.images,
                                      price: product.price,
                                      isActive: product.isActive,
                                      fanpagePositions:
                                        product.fanpagePositions,
                                      position: product.position,
                                      fanpages: product.fanpages,
                                    })
                                  );

                                  // Kiểm tra số lượng thiết lập
                                  const groupPlatform = group.fanpage.platform || "facebook";
                                  const groupSelectedAccount = toSelectedAccountPayload(group.fanpage);
                                  if (totalConfigs === 0) {
                                    // Không có thiết lập -> tạo mới
                                    navigate("/product-auto-post", {
                                      state: {
                                        config: {
                                          inputType: "products",
                                          platform: groupPlatform,
                                          selectedAccounts: [groupSelectedAccount],
                                          fanpageID: groupPlatform === "facebook" ? group.fanpage.id : "",
                                          fanpageName: group.fanpage.name,
                                          accessToken:
                                            groupPlatform === "facebook" ? group.fanpage.access_token : "",
                                          fanpageProducts: fanpageProducts,
                                        },
                                      },
                                    });
                                  } else if (totalConfigs === 1) {
                                    // Chỉ có 1 thiết lập -> điều hướng trực tiếp
                                    const existingConfig = configs[0];
                                    navigate("/product-auto-post", {
                                      state: {
                                        config: {
                                          id: existingConfig.id,
                                          inputType: "products",
                                          platform: groupPlatform,
                                          selectedAccounts: [groupSelectedAccount],
                                          fanpageID: groupPlatform === "facebook" ? group.fanpage.id : "",
                                          fanpageName: group.fanpage.name,
                                          accessToken:
                                            groupPlatform === "facebook" ? group.fanpage.access_token : "",
                                          fanpageProducts: fanpageProducts,
                                        },
                                      },
                                    });
                                  } else {
                                    // Có nhiều thiết lập -> hiển thị modal chọn
                                    setConfigSelectionModal({
                                      isOpen: true,
                                      fanpageId: group.fanpage.id,
                                      fanpageName: group.fanpage.name,
                                      platform: groupPlatform,
                                      configs: configs,
                                      fanpageProducts: fanpageProducts,
                                    });
                                  }
                                }}
                                className={buttonClass}
                                title={`${buttonText} cho fanpage này`}
                              >
                                {buttonIcon}
                                <span className="hidden sm:inline">
                                  {buttonText}
                                </span>
                                <span className="sm:hidden">
                                  {totalConfigs === 0 ? "Tạo" : "Quản lý"}
                                </span>
                              </button>
                            );
                          })()}

                          {/* Nút sắp xếp cho nhóm này - chỉ hiển thị khi có sản phẩm */}
                          {hasProducts && (
                            <button
                              onClick={() =>
                                toggleSortModeForFanpage(fanpageId)
                              }
                              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                isSortMode[fanpageId]
                                  ? "bg-white text-blue-600 border-white"
                                  : "bg-transparent text-white border-white border-opacity-50 hover:border-opacity-100"
                              }`}
                            >
                              {isSortMode[fanpageId] ? "Lưu thứ tự" : "Sắp xếp"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Nội dung sản phẩm */}
                    <div className="p-6">
                      {hasProducts ? (
                        <>
                          {isSortMode[fanpageId] && (
                            <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 text-blue-700 rounded">
                              <p className="text-sm font-medium">
                                Kéo và thả để sắp xếp thứ tự đăng bài cho{" "}
                                {group.fanpage.name}
                              </p>
                            </div>
                          )}

                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) =>
                              handleGroupedDragEnd(event, fanpageId)
                            }
                          >
                            <SortableContext
                              items={filteredProducts.map((item) => item.id)}
                              strategy={rectSortingStrategy}
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {filteredProducts.map((product, index) => (
                                  <SortableProductItem
                                    key={product.id}
                                    product={product}
                                    index={index}
                                    isSortMode={isSortMode[fanpageId]}
                                    onProductClick={handleProductClick}
                                    isEditMode={isEditMode}
                                    isSelected={selectedProducts.has(product.id)}
                                    onSelectProduct={handleSelectProduct}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <FiUsers className="mx-auto text-4xl text-gray-300 mb-3" />
                          <h4 className="text-lg font-medium text-gray-600 mb-2">
                            Chưa có sản phẩm nào
                          </h4>
                          <p className="text-gray-500 text-sm mb-4">
                            Fanpage này chưa có sản phẩm nào được thêm vào.
                          </p>
                          <button
                            onClick={() => navigate("/product-create")}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            Tạo sản phẩm đầu tiên
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            {Object.keys(groupedProducts).length === 0 && (
              <div className="text-center py-12">
                <FiUsers className="mx-auto text-6xl text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Chưa có sản phẩm nào
                </h3>
                <p className="text-gray-500">
                  Hãy tạo sản phẩm đầu tiên để bắt đầu!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal chọn thiết lập tự động */}
      {configSelectionModal.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setConfigSelectionModal({
                ...configSelectionModal,
                isOpen: false,
              });
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Chọn thiết lập cho {configSelectionModal.fanpageName}
                </h3>
                <button
                  onClick={() =>
                    setConfigSelectionModal({
                      ...configSelectionModal,
                      isOpen: false,
                    })
                  }
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <FiXCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {configSelectionModal.configs.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Fanpage này có {configSelectionModal.configs.length} thiết
                    lập tự động. Chọn thiết lập bạn muốn quản lý:
                  </p>

                  {configSelectionModal.configs.map((config, index) => {
                    const isActive = config.autoPostEnabled;
                    const postTimes =
                      config.times || config.selectedTimes || [];
                    // Xử lý selectedDays - có thể là object hoặc array
                    let postDays = [];
                    if (config.selectedDays) {
                      if (Array.isArray(config.selectedDays)) {
                        postDays = config.selectedDays;
                      } else if (typeof config.selectedDays === "object") {
                        postDays = Object.keys(config.selectedDays).filter(
                          (day) => config.selectedDays[day]
                        );
                      }
                    }

                    // Format giờ đăng
                    const formatPostTimes =
                      postTimes.length > 0
                        ? postTimes
                            .map((time) => {
                              const [hour, minute] = time.split(":");
                              return `${hour}:${minute}`;
                            })
                            .join(", ")
                        : "Chưa thiết lập";

                    // Format ngày đăng theo thứ tự đúng
                    const dayNames = {
                      monday: "Thứ 2",
                      tuesday: "Thứ 3",
                      wednesday: "Thứ 4",
                      thursday: "Thứ 5",
                      friday: "Thứ 6",
                      saturday: "Thứ 7",
                      sunday: "Chủ nhật",
                    };

                    // Thứ tự đúng của các ngày trong tuần
                    const dayOrder = [
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                      "sunday",
                    ];

                    const formatPostDays =
                      postDays.length > 0
                        ? dayOrder
                            .filter((day) => postDays.includes(day))
                            .map((day) => dayNames[day] || day)
                            .join(", ")
                        : "Chưa thiết lập";

                    return (
                      <div
                        key={config.id || index}
                        className={`border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
                          isActive
                            ? "border-green-400 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-150"
                            : "border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150"
                        }`}
                        onClick={() => {
                          const modalPlatform = config.platform || configSelectionModal.platform || "facebook";
                          const modalAccount = toSelectedAccountPayload({
                            platform: modalPlatform,
                            accountId: configSelectionModal.fanpageId,
                            name: configSelectionModal.fanpageName,
                          });
                          navigate("/product-auto-post", {
                            state: {
                              config: {
                                id: config.id,
                                inputType: "products",
                                platform: modalPlatform,
                                selectedAccounts: [modalAccount],
                                fanpageID: modalPlatform === "facebook" ? configSelectionModal.fanpageId : "",
                                fanpageName: configSelectionModal.fanpageName,
                                accessToken: modalPlatform === "facebook" ? config.accessToken : "",
                                fanpageProducts:
                                  configSelectionModal.fanpageProducts,
                              },
                            },
                          });
                          setConfigSelectionModal({
                            ...configSelectionModal,
                            isOpen: false,
                          });
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${
                                  isActive
                                    ? "bg-green-500 text-white"
                                    : "bg-gray-500 text-white"
                                }`}
                              >
                                {isActive ? "🟢 Đang chạy" : "⚫ Đã tắt"}
                              </span>
                              <span className="text-sm font-medium text-gray-700 bg-white px-2 py-1 rounded-md shadow-sm">
                                Thiết lập #{index + 1}
                              </span>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-600">🕐</span>
                                <span className="text-sm font-medium text-gray-700">
                                  Giờ đăng:
                                </span>
                                <span className="text-sm text-gray-600 bg-blue-50 px-2 py-1 rounded-md">
                                  {formatPostTimes}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-purple-600">📅</span>
                                <span className="text-sm font-medium text-gray-700">
                                  Ngày đăng:
                                </span>
                                <span className="text-sm text-gray-600 bg-purple-50 px-2 py-1 rounded-md">
                                  {formatPostDays}
                                </span>
                              </div>

                              {config.language && (
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600">🌐</span>
                                  <span className="text-sm font-medium text-gray-700">
                                    Ngôn ngữ:
                                  </span>
                                  <span className="text-sm text-gray-600 bg-green-50 px-2 py-1 rounded-md">
                                    {config.language === "vi"
                                      ? "Tiếng Việt"
                                      : "English"}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="ml-4 flex items-center">
                            <div className="bg-white rounded-full p-2 shadow-sm">
                              <svg
                                className="h-5 w-5 text-gray-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">
                    Chưa có thiết lập tự động nào
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => {
                  const newConfigPlatform = configSelectionModal.platform || "facebook";
                  const newConfigAccount = toSelectedAccountPayload({
                    platform: newConfigPlatform,
                    accountId: configSelectionModal.fanpageId,
                    name: configSelectionModal.fanpageName,
                  });
                  navigate("/product-auto-post", {
                    state: {
                      config: {
                        inputType: "products",
                        platform: newConfigPlatform,
                        selectedAccounts: [newConfigAccount],
                        fanpageID: newConfigPlatform === "facebook" ? configSelectionModal.fanpageId : "",
                        fanpageName: configSelectionModal.fanpageName,
                        fanpageProducts: configSelectionModal.fanpageProducts,
                      },
                    },
                  });
                  setConfigSelectionModal({
                    ...configSelectionModal,
                    isOpen: false,
                  });
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Tạo thiết lập mới
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận xóa */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FiTrash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Xác nhận xóa sản phẩm
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa <span className="font-semibold text-red-600">{selectedProducts.size}</span> sản phẩm đã chọn?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Hành động này không thể hoàn tác.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteSelectedProducts}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ProductsScreen;
